import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import './_whatsAppModal.scss';

const BUSINESS_TYPES = [
  { value: 'OTHER', label: 'Other' },
  { value: 'AUTO', label: 'Automotive' },
  { value: 'BEAUTY', label: 'Beauty' },
  { value: 'APPAREL', label: 'Apparel & Fashion' },
  { value: 'EDU', label: 'Education' },
  { value: 'ENTERTAIN', label: 'Entertainment' },
  { value: 'EVENT_PLAN', label: 'Event Planning' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'GOVT', label: 'Government' },
  { value: 'HOTEL', label: 'Hotel & Lodging' },
  { value: 'HEALTH', label: 'Healthcare' },
  { value: 'NONPROFIT', label: 'Non-Profit' },
  { value: 'PROF_SERVICES', label: 'Professional Services' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'ALCOHOL', label: 'Alcohol' },
  { value: 'ONLINE_GAMBLING', label: 'Online Gambling' },
  { value: 'PHYSICAL_GAMBLING', label: 'Physical Gambling' },
  { value: 'OTC_DRUGS', label: 'OTC Drugs' },
];

const UpdateBusinessModal = ({ show, onHide, initialData, onSubmit }) => {
  // Initialize formData with default empty values
  const [formData, setFormData] = useState({
    messaging_product: 'whatsapp',
    address: '',
    description: '',
    vertical: '',
    about: '',
    email: '',
    websites: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Update formData when initialData changes or when modal is shown
  useEffect(() => {
    // Check if initialData exists and has required properties
    if (initialData && typeof initialData === 'object' && Object.keys(initialData).length > 0) {
      try {
        // Create a safe copy of initialData with fallbacks for missing properties
        const safeData = {
          messaging_product: initialData.messaging_product || 'whatsapp',
          address: initialData.address || '',
          description: initialData.description || '',
          vertical: initialData.vertical || '',
          about: initialData.about || '',
          email: initialData.email || '',
          websites: Array.isArray(initialData.websites) && initialData.websites.length > 0
            ? initialData.websites[0]
            : (initialData.websites || ''),
        };

        setFormData(safeData);
      } catch (error) {
        console.error('Error processing initialData:', error);
        // Keep the default values if there's an error
      }
    }
  }, [initialData, show]); // Also trigger when show changes to true

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate email format if provided (not required)
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Only vertical/business type is required
    if (!formData.vertical) {
      newErrors.vertical = 'Business type is required';
    }

    // Only validate website format if provided
    if (typeof formData.websites === 'string' && formData.websites.trim() !== '') {
      if (!formData.websites.match(/^https?:\/\/.+/)) {
        newErrors.websites = 'Website URL must start with https://';
      }
    }
    else if (Array.isArray(formData.websites) && formData.websites.length > 0) {
      const websiteUrl = formData.websites[0];
      if (websiteUrl && !websiteUrl.match(/^https?:\/\/.+/)) {
        newErrors.websites = 'Website URL must start with https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        websites: formData.websites
          ? typeof formData.websites === 'string'
            ? [formData.websites]
            : formData.websites
          : [],
      };
      setLoading(true);
      onSubmit(submitData, { setLoading });
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="md"
      aria-labelledby="business-profile-modal"
      centered
      className="wa-business-profile-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="business-profile-modal">Business Profile</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Row className="mb-4">
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <Form.Group className="wa-form-group">
                <Form.Label>Messaging Product</Form.Label>
                <Form.Control
                  type="text"
                  name="messaging_product"
                  value={formData.messaging_product}
                  disabled
                  className="wa-disabled-field"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="wa-form-group">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder="Enter business email"
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="wa-form-group mb-4">
            <Form.Label>Business Address</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="address"
              value={formData.address}
              onChange={handleChange}
              isInvalid={!!errors.address}
              placeholder="Enter complete business address"
              rows={3}
            />
            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-4">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              isInvalid={!!errors.description}
              placeholder="Enter business description"
              rows={2}
            />
            <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-4">
            <Form.Label className="wa-required-label">Business Type</Form.Label>
            <Form.Control
              as="select"
              name="vertical"
              value={formData.vertical}
              onChange={handleChange}
              isInvalid={!!errors.vertical}
              className="wa-select"
            >
              <option value="">Select Business Type</option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.vertical}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-4">
            <Form.Label>About</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="about"
              value={formData.about}
              onChange={handleChange}
              isInvalid={!!errors.about}
              placeholder="Enter information about your business"
              rows={2}
            />
            <Form.Control.Feedback type="invalid">{errors.about}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-4">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="url"
              name="websites"
              value={formData.websites}
              onChange={handleChange}
              isInvalid={!!errors.websites}
              placeholder="Enter business website URL (https://...)"
            />
            <Form.Control.Feedback type="invalid">{errors.websites}</Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            className="wa-button-secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="wa-button-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateBusinessModal;
